import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import LmsAuthShell from "../components/LmsAuthShell";
import LmsFormField from "../components/LmsFormField";
import LmsRoleToggle from "../components/LmsRoleToggle";
import { registerLmsUser } from "../controllers/lmsAuthController";
import { setLmsCredObj } from "../../../slices/auth/authenticationSlice";
import errorHandler from "../../../utils/errorHandler";
import { LMS_ROLES } from "../utils/lmsRole";
import { buildLmsCredential } from "../utils/lmsSession";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: LMS_ROLES.teacher,
  teacherType: "school",
  schoolName: "",
  gradeClass: "",
  semester: "",
};

const LmsRegisterPage = () => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "password" || name === "confirmPassword") {
      setPasswordError("");
    }
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleRoleChange = (role) => {
    setFormData((current) => ({ ...current, role }));
  };

  const buildPayload = () => {
    const name = `${formData.firstName} ${formData.lastName}`.trim();
    const payload = {
      name,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      group: "adult",
      role: formData.role,
      lmsRole: formData.role,
    };

    if (formData.role === LMS_ROLES.teacher) {
      payload.teacherType = formData.teacherType;
      if (formData.teacherType === "school") {
        payload.schoolName = formData.schoolName;
        payload.gradeClass = formData.gradeClass;
        payload.semester = formData.semester;
      }
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildPayload();
      const response = await dispatch(registerLmsUser(payload)).unwrap();
      const lmsCredential = buildLmsCredential(response, {
        lmsRole: formData.role,
      });

      if (lmsCredential.token && lmsCredential.refreshToken) {
        dispatch(setLmsCredObj(lmsCredential));
      }

      toast.success("Registration successful. Check your email for a verification code.");
      navigate(`/lms/verify-email?email=${encodeURIComponent(formData.email)}&from=register`, {
        replace: true,
        state: { fromRegister: true, email: formData.email },
      });
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isTeacher = formData.role === LMS_ROLES.teacher;
  const isSchoolTeacher = isTeacher && formData.teacherType === "school";

  return (
    <LmsAuthShell
      title="Create your learning space"
      subtitle="Start as a teacher or parent and build the first steps of a reading-growth journey."
    >
      <form className="lms-form" onSubmit={handleSubmit}>
        <LmsRoleToggle value={formData.role} onChange={handleRoleChange} />

        <div className="lms-two-column">
          <LmsFormField
            label="First name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            autoComplete="given-name"
          />
          <LmsFormField
            label="Last name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            autoComplete="family-name"
          />
        </div>

        <LmsFormField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        <LmsFormField
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          autoComplete="new-password"
        />
        <LmsFormField
          label="Confirm password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          autoComplete="new-password"
        />
        {passwordError && (
          <p className="lms-field-error" role="alert">
            {passwordError}
          </p>
        )}

        {isTeacher && (
          <label className="lms-field">
            <span>Teacher type</span>
            <select name="teacherType" value={formData.teacherType} onChange={handleChange}>
              <option value="school">School teacher</option>
              <option value="independent">Independent teacher</option>
            </select>
          </label>
        )}

        {isSchoolTeacher && (
          <>
            <LmsFormField
              label="School name"
              name="schoolName"
              value={formData.schoolName}
              onChange={handleChange}
            />
            <div className="lms-two-column">
              <LmsFormField
                label="Grade/Class"
                name="gradeClass"
                value={formData.gradeClass}
                onChange={handleChange}
              />
              <LmsFormField
                label="Semester"
                name="semester"
                value={formData.semester}
                onChange={handleChange}
              />
            </div>
          </>
        )}

        <button className="lms-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create account"}
        </button>
        <p className="lms-auth-switch">
          Already have an account? <Link to="/lms/login">Login</Link>
        </p>
      </form>
    </LmsAuthShell>
  );
};

export default LmsRegisterPage;
